import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const generateSessionId = () => {
  const existing = localStorage.getItem('hfit_sales_session');
  if (existing) return existing;
  const id = `sales-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  localStorage.setItem('hfit_sales_session', id);
  return id;
};

const SUGGESTED = [
  "How much does it cost?",
  "What's included in the Pro plan?",
  "How fast can I set this up?",
  "Do I need a contract?",
];

export default function SalesChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: "Hi! I'm Sunny, your Simplified Helpdesk guide. Ask me anything about pricing, setup, or how the platform works. What brings you here today?",
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionId = useRef(generateSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    setMessages((m) => [...m, { role: 'user', text: trimmed }]);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(`${API}/sales-chat`, {
        session_id: sessionId.current,
        message: trimmed,
      });
      setMessages((m) => [...m, { role: 'assistant', text: res.data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Connection issue. Please try again.';
      setMessages((m) => [
        ...m,
        { role: 'assistant', text: `Sorry — ${detail}` },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher Button */}
      {!open && (
        <button
          data-testid="sales-chat-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-6 z-50 bg-gray-900 hover:bg-gray-800 text-white rounded-full shadow-xl px-5 py-4 flex items-center gap-2 transition-all hover:scale-105"
          aria-label="Chat with Sunny, our AI sales assistant"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium text-sm">Chat with Sunny</span>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="sales-chat-panel"
          className="fixed bottom-20 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9">
                <svg viewBox="0 0 48 48" className="w-9 h-9">
                  <rect x="8"  y="11" width="32" height="6" rx="2" fill="#111827" />
                  <rect x="8"  y="21" width="22" height="6" rx="2" fill="#374151" />
                  <rect x="8"  y="31" width="12" height="6" rx="2" fill="#FCD34D" />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-sm">Sunny</div>
                <div className="text-xs text-gray-500">AI sales assistant • online</div>
              </div>
            </div>
            <button
              data-testid="sales-chat-close"
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-700 transition"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  data-testid={`sales-chat-msg-${m.role}`}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-gray-900 text-white rounded-br-sm'
                      : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-4 py-2.5 rounded-2xl rounded-bl-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 bg-gray-50">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  data-testid={`sales-chat-suggestion-${s.slice(0, 10)}`}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full text-gray-700 hover:bg-gray-100 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="px-4 py-3 border-t border-gray-200 bg-white flex items-center gap-2"
          >
            <Input
              data-testid="sales-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about pricing, setup, anything..."
              className="flex-1 border-gray-200 focus-visible:ring-gray-900"
              disabled={sending}
            />
            <Button
              data-testid="sales-chat-send"
              type="submit"
              disabled={sending || !input.trim()}
              className="bg-gray-900 hover:bg-gray-800 text-white px-3"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
